import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import toast from "react-hot-toast";

import SvgLoadingAnimation from "../../../../components/loading";
import IconButton from "../../../../components/button/IconButton";
import HelpHint from "../../../../components/help-hint/HelpHint";
import ConfirmModal from "../../../../components/modal/ConfirmModal";
import Table, { TableColumn } from "../../../../components/table/Table";
import FilterChipBar from "../../../../components/filter-chip/FilterChipBar";
import { TopAppBarSubRow } from "../../../../components/top-app-bar/TopAppBarHome";
import ShotTableRow from "./ShotTableRow";
import ShotMoveWizardModal from "./ShotMoveWizardModal";
import {
  Shot,
  useCreateShotMutation,
  useDeleteShotMutation,
  useDeleteShotRatingMutation,
  useLazyGetShotsQuery,
  useRateShotMutation,
  useUpdateShotMutation,
} from "../../../../app/services/shots";
import {
  ShotTag,
  useGetProjectQuery,
  useUpdateProjectMutation,
} from "../../../../app/services/projects";
import { useGetProjectMembershipQuery } from "../../../../app/accounts/memberships";
import {
  isClientGroupPosition,
  isPrimaryClientPosition,
  isProducerGroupPosition,
} from "../../../../utils/memberPositionUtils";
import {
  getShotTableStatusKey,
  getTableStatusKey,
  isDeliveryDateEditable,
  parseTableStatusLabel,
  shotHasMedia,
  TABLE_STATUS_TO_SHOT_STATUS,
} from "../../../../constant/shotTableStatus";
import { ShotStatus } from "../../../../types/home";
import {
  buildShotsTsv,
  isHeaderRow,
  isTabularClipboardText,
  parseClipboardTable,
  parsePastedDate,
  sortShotRows,
  sortShotsByInitialOrder,
  ShotPatch,
  ShotTableSortField,
  ShotTableSortOrder,
  ShotTableSortState,
} from "./shotTableUtils";

const PAGE_LIMIT = 100;
const MAX_PAGES = 50;
/** 行アクション列の幅（操作はすべて 3 点リーダーに畳むので 1 アイコン分） */
const ACTIONS_COL_WIDTH = 44;

/** 表の使い方（? アイコンのホバーで出す。編集権限がある場合のみ） */
const TABLE_HELP = `Excel / スプレッドシートから「ショット名・ステータス・次の納品日」の列順で貼り付けできます。
セルを編集中に貼り付けると、その行から順に上書きされます。
列の境界はドラッグで幅調整、ダブルクリックで内容に合わせて自動調整できます。`;

const BulkOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.6);
  z-index: 2;
`;

/**
 * 行追加 input の高密度スタイル。
 * セル内コントロールは枠を持たず、フォーカス中はセル側の :focus-within
 * リングで示す（グローバル input のスタイルを上書き）。
 */
const ADD_INPUT_STYLE: React.CSSProperties = {
  flex: 1,
  padding: "2px 8px",
  fontSize: 13,
  lineHeight: "150%",
  height: 26,
  border: "none",
  outline: "none",
  boxShadow: "none",
  background: "transparent",
};

/**
 * 全ショットを表形式で一覧・編集するスケジュールビュー。
 *
 * - テイク未納品のショットも含めて全件をロード（include_empty）
 * - 初期表示はポジション別のステータス優先順（sortShotsByInitialOrder 参照）
 * - 列ヘッダークリックでソート、列境界のドラッグで幅調整（ダブルクリックで自動調整）
 * - セル編集（ショット名 / ステータス / 次の納品日 / 評価）
 * - 行追加（ショット作成）・行削除（準備中は確認なしで即削除）
 * - ショット間移動（メディア・コメントのウィザード移動。プロデューサー側 + クライアント責任者）
 * - Excel / CSV のコピー & 貼り付け（貼り付けはプロデューサー側のみ）
 *
 * 権限:
 * - プロデューサー側（!read_only）: フル編集
 * - クライアント側（!read_only）: ショット追加・改名・準備中行の削除・
 *   限定ステータス遷移（チェックバック / 保留 → 完了、保留 / 完了 → チェックバック）
 */
const ShotTable: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();

  const { data: membershipData } = useGetProjectMembershipQuery(projectId || "", {
    skip: !projectId,
  });

  // タグフィルター（ホーム「完了」タブと同じ shot_tags を使用）
  const { data: projectData } = useGetProjectQuery(
    { project_id: projectId || "" },
    { skip: !projectId },
  );
  const shotTags: ShotTag[] = projectData?.data?.project?.shot_tags || [];
  const [selectedTagId, setSelectedTagId] = useState<string | null>(null);
  const [updateProject] = useUpdateProjectMutation();
  const memberPosition = (membershipData?.data?.position || "").toUpperCase();
  const isReadOnly = membershipData?.data?.is_read_only ?? true;
  const isClientGroup = isClientGroupPosition(memberPosition);
  // プロデューサー側 = フル編集（納品日・全ステータス・全行削除・一括貼り付け）
  const canEdit = isProducerGroupPosition(memberPosition) && !isReadOnly;
  // クライアント側 = 限定編集（ショット追加・改名・準備中削除・限定ステータス遷移）
  const canClientEdit = isClientGroup && !isReadOnly;
  const canAddShot = canEdit || canClientEdit;
  // 評価入力はクライアント側のみ。プロデューサー側は全体平均の閲覧のみ
  const canRateInline = isClientGroup && (membershipData?.data?.can_rate ?? false);
  // ショット間移動: プロデューサー側 + クライアント責任者（CLIENT）。レビュアーは対象外
  const canMove =
    canEdit || (isPrimaryClientPosition(memberPosition) && !isReadOnly);

  const [fetchShots] = useLazyGetShotsQuery();
  const [updateShot] = useUpdateShotMutation();
  const [createShot] = useCreateShotMutation();
  const [deleteShot] = useDeleteShotMutation();
  const [rateShot] = useRateShotMutation();
  const [deleteShotRating] = useDeleteShotRatingMutation();

  const [rows, setRows] = useState<Shot[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  // null = 初期ソート（ポジション別のステータス優先順）。ヘッダークリックで列ソートへ切り替わる
  const [sortState, setSortState] = useState<ShotTableSortState | null>(null);
  const [newShotName, setNewShotName] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Shot | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isBulkApplying, setIsBulkApplying] = useState(false);
  // ショット間移動ウィザードの移動元ショット（null = 非表示）
  const [moveSourceShot, setMoveSourceShot] = useState<Shot | null>(null);

  // タグ選択時は読み込み済みの全行からクライアント側で絞り込む
  const visibleRows = useMemo(
    () =>
      selectedTagId
        ? rows.filter((r) => r.tag_ids?.includes(selectedTagId))
        : rows,
    [rows, selectedTagId],
  );

  // 貼り付け・コピー処理用に最新の表示行を参照できるようにする
  const visibleRowsRef = useRef<Shot[]>(visibleRows);
  useEffect(() => {
    visibleRowsRef.current = visibleRows;
  }, [visibleRows]);

  // タグの並び替えを完了タブと同様にプロジェクトへ保存する
  const handleTagReorder = useCallback(
    (reorderedTags: ShotTag[]) => {
      if (!projectId) return;
      const payload = reorderedTags.map((t) => ({
        id: t.id,
        name: t.name,
        sort_rule: t.sort_rule,
      }));
      updateProject({ project_id: projectId, shot_tags: payload });
    },
    [projectId, updateProject],
  );

  // ── データロード（全ページを順次取得） ──
  const loadAllShots = useCallback(async () => {
    if (!projectId) return;
    setIsLoading(true);
    setLoadError(false);
    try {
      const all: Shot[] = [];
      let offset = 0;
      for (let page = 0; page < MAX_PAGES; page++) {
        const res = await fetchShots({
          project_id: projectId,
          include_empty: true,
          limit: PAGE_LIMIT,
          offset,
          sort_by: "updated_at",
          order: "desc",
        }).unwrap();
        all.push(...res.data.shots);
        if (!res.data.has_next) break;
        offset = res.data.next_offset ?? all.length;
      }
      // 初期表示はポジション別のステータス優先順（同ステータス内は最終更新日の新しい順）
      setRows(sortShotsByInitialOrder(all, isClientGroup));
      setSortState(null);
    } catch {
      setLoadError(true);
    } finally {
      setIsLoading(false);
    }
  }, [projectId, fetchShots, isClientGroup]);

  useEffect(() => {
    loadAllShots();
  }, [loadAllShots]);

  // ── 行操作 ──
  const applyShotUpdate = useCallback((updated: Shot) => {
    setRows((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
  }, []);

  const patchRowFields = useCallback((shotId: string, fields: Partial<Shot>) => {
    setRows((prev) => prev.map((r) => (r.id === shotId ? { ...r, ...fields } : r)));
  }, []);

  const appendShot = useCallback((shot: Shot) => {
    setRows((prev) => [...prev, shot]);
  }, []);

  const handlePatch = useCallback(
    async (shotId: string, patch: ShotPatch) => {
      if (!projectId) return;
      try {
        const res = await updateShot({
          project_id: projectId,
          shot_id: shotId,
          ...patch,
        }).unwrap();
        applyShotUpdate(res.data.shot);
      } catch {
        toast.error("ショットの更新に失敗しました");
      }
    },
    [projectId, updateShot, applyShotUpdate],
  );

  // ── ソート ──
  const handleSort = useCallback(
    (field: ShotTableSortField) => {
      const order: ShotTableSortOrder =
        sortState?.field === field && sortState.order === "asc" ? "desc" : "asc";
      setSortState({ field, order });
      setRows((prev) => sortShotRows(prev, field, order));
    },
    [sortState],
  );

  // ── 行追加 ──
  const handleCreate = useCallback(async () => {
    const name = newShotName.trim();
    if (!projectId || !name || isCreating) return;
    setIsCreating(true);
    try {
      const res = await createShot({
        project_id: projectId,
        shot_name: name,
        status: ShotStatus.CLIENT_PENDING, // テイク未納品のため表示は「準備中」
      }).unwrap();
      appendShot(res.data.shot);
      setNewShotName("");
    } catch {
      toast.error("ショットの作成に失敗しました");
    } finally {
      setIsCreating(false);
    }
  }, [projectId, newShotName, isCreating, createShot, appendShot]);

  const handleAddKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.nativeEvent.isComposing) {
      e.preventDefault();
      handleCreate();
    }
  };

  // ── 行削除 ──
  const deletingIdsRef = useRef<Set<string>>(new Set());

  const performDelete = useCallback(
    async (shot: Shot) => {
      if (!projectId || deletingIdsRef.current.has(shot.id)) return;
      deletingIdsRef.current.add(shot.id);
      try {
        await deleteShot({ project_id: projectId, shot_id: shot.id }).unwrap();
        setRows((prev) => prev.filter((r) => r.id !== shot.id));
      } catch {
        toast.error("ショットの削除に失敗しました");
      } finally {
        deletingIdsRef.current.delete(shot.id);
      }
    },
    [projectId, deleteShot],
  );

  const handleRequestDelete = useCallback(
    (shot: Shot) => {
      // 準備中（テイク未納品）はやり直しが容易なため、確認モーダルなしで即削除する
      if (getShotTableStatusKey(shot) === "preparing") {
        void performDelete(shot);
        return;
      }
      setDeleteTarget(shot);
    },
    [performDelete],
  );

  const handleDeleteConfirmed = useCallback(async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    await performDelete(deleteTarget);
    setDeleteTarget(null);
    setIsDeleting(false);
  }, [deleteTarget, performDelete]);

  // ── 評価（クライアントのセル内入力） ──
  const handleRate = useCallback(
    async (shot: Shot, rating: number) => {
      if (!projectId) return;
      try {
        const res = await rateShot({
          project_id: projectId,
          shot_id: shot.id,
          rating,
        }).unwrap();
        patchRowFields(shot.id, {
          my_rating: rating,
          average_rating: res.data.average_rating,
        });
      } catch {
        toast.error("評価の更新に失敗しました");
      }
    },
    [projectId, rateShot, patchRowFields],
  );

  const handleClearRating = useCallback(
    async (shot: Shot) => {
      if (!projectId) return;
      try {
        const res = await deleteShotRating({
          project_id: projectId,
          shot_id: shot.id,
        }).unwrap();
        patchRowFields(shot.id, {
          my_rating: null,
          average_rating: res.data.average_rating,
        });
      } catch {
        toast.error("評価の取り消しに失敗しました");
      }
    },
    [projectId, deleteShotRating, patchRowFields],
  );

  // ── プレビューへ遷移 ──
  const handleOpenPreview = useCallback(
    (shot: Shot) => {
      if (!projectId) return;
      navigate(`/${projectId}/${shot.id}`, { state: { shot } });
    },
    [projectId, navigate],
  );

  // ── コピー ──
  const copyTableToClipboard = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(buildShotsTsv(visibleRowsRef.current));
      toast.success("表をコピーしました");
    } catch {
      toast.error("コピーに失敗しました");
    }
  }, []);

  const handleCopy = useCallback((e: React.ClipboardEvent) => {
    const target = e.target as HTMLElement;
    // セル編集中のテキストコピーはブラウザ既定に任せる
    if (target.closest("input, textarea")) return;
    e.preventDefault();
    e.clipboardData.setData("text/plain", buildShotsTsv(visibleRowsRef.current));
    toast.success("表をコピーしました");
  }, []);

  // ── 貼り付け（Excel / CSV） ──
  const applyPastedGrid = useCallback(
    async (grid: string[][], anchorIndex: number | null) => {
      if (!projectId) return;
      let body = grid;
      if (body.length > 0 && isHeaderRow(body[0])) {
        body = body.slice(1);
      }
      body = body.filter((cells) => cells.some((c) => c !== ""));
      if (body.length === 0) return;

      setIsBulkApplying(true);
      // 貼り付けの上書き対象は表示中の行（タグで絞り込み中はその並び）
      const snapshot = visibleRowsRef.current;
      let updated = 0;
      let created = 0;
      let failed = 0;

      for (let i = 0; i < body.length; i++) {
        const cells = body[i];
        const name = (cells[0] ?? "").trim();
        const statusKey = parseTableStatusLabel(cells[1] ?? "");
        const dateResult = parsePastedDate(cells[2] ?? "");

        const targetIndex = anchorIndex == null ? null : anchorIndex + i;
        const target =
          targetIndex != null && targetIndex < snapshot.length
            ? snapshot[targetIndex]
            : null;

        try {
          if (target) {
            // 既存行を上書き
            const patch: ShotPatch = {};
            if (name && name !== target.shot_name) {
              patch.shot_name = name;
            }

            let nextStatus = target.status;
            if (statusKey) {
              nextStatus = TABLE_STATUS_TO_SHOT_STATUS[statusKey];
              if (nextStatus !== target.status) {
                patch.status = nextStatus;
              }
            }

            // 納品日は「準備中・制作中」のみ設定可。それ以外はクリアする
            const finalKey = getTableStatusKey(nextStatus, shotHasMedia(target));
            if (dateResult.ok && dateResult.iso !== undefined) {
              const newDate = isDeliveryDateEditable(finalKey) ? dateResult.iso : null;
              if (newDate !== target.next_delivery_date) {
                patch.next_delivery_date = newDate;
              }
            }

            if (Object.keys(patch).length > 0) {
              const res = await updateShot({
                project_id: projectId,
                shot_id: target.id,
                ...patch,
              }).unwrap();
              applyShotUpdate(res.data.shot);
              updated++;
            }
          } else {
            // 新規ショットとして追加
            if (!name) continue;
            const status = statusKey
              ? TABLE_STATUS_TO_SHOT_STATUS[statusKey]
              : ShotStatus.CLIENT_PENDING;
            const res = await createShot({
              project_id: projectId,
              shot_name: name,
              status,
            }).unwrap();
            let shot = res.data.shot;
            // POST は納品日を受け付けないため、必要なら続けて PATCH する
            const key = getShotTableStatusKey(shot);
            if (dateResult.ok && dateResult.iso && isDeliveryDateEditable(key)) {
              const res2 = await updateShot({
                project_id: projectId,
                shot_id: shot.id,
                next_delivery_date: dateResult.iso,
              }).unwrap();
              shot = res2.data.shot;
            }
            appendShot(shot);
            created++;
          }
        } catch {
          failed++;
        }
      }

      setIsBulkApplying(false);
      const summary = [
        updated > 0 ? `${updated}件更新` : null,
        created > 0 ? `${created}件追加` : null,
        failed > 0 ? `${failed}件失敗` : null,
      ]
        .filter(Boolean)
        .join(" / ");
      if (failed > 0) {
        toast.error(summary || "貼り付けの反映に失敗しました");
      } else if (summary) {
        toast.success(summary);
      }
    },
    [projectId, updateShot, createShot, applyShotUpdate, appendShot],
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      if (!canEdit || isBulkApplying) return;
      const text = e.clipboardData.getData("text/plain");
      // 単一値の貼り付けは入力欄の既定動作に任せる
      if (!text || !isTabularClipboardText(text)) return;
      e.preventDefault();
      const grid = parseClipboardTable(text);
      const rowEl = (e.target as HTMLElement).closest?.(
        "tr[data-row-index]",
      ) as HTMLElement | null;
      const anchorIndex = rowEl ? Number(rowEl.dataset.rowIndex) : null;
      applyPastedGrid(grid, anchorIndex);
    },
    [canEdit, isBulkApplying, applyPastedGrid],
  );

  // ── レンダリング ──
  if (isLoading) {
    return (
      <div className="u-col-center u-w100 u-mt48">
        <SvgLoadingAnimation />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="u-col-center u-w100 u-mt48">
        <p className="u-text-green">ショットデータの取得に失敗しました</p>
      </div>
    );
  }

  const sortHeader = (label: string, field: ShotTableSortField) => (
    <>
      <span>{label}</span>
      {sortState?.field === field && (
        <span className="material-symbols-rounded" style={{ fontSize: 14 }}>
          {sortState.order === "asc" ? "arrow_upward" : "arrow_downward"}
        </span>
      )}
    </>
  );

  const columns: TableColumn[] = [
    // 行頭: Preview ページへのリンク
    { key: "open", width: 36, minWidth: 36, resizable: false },
    {
      key: "shot_name",
      header: sortHeader("ショット名", "shot_name"),
      minWidth: 120,
      onHeaderClick: () => handleSort("shot_name"),
      headerTitle: "クリックでソート",
    },
    {
      key: "status",
      header: sortHeader("ステータス", "status"),
      // 最長の「チェックバック」(7文字) が省略されない幅
      width: 140,
      minWidth: 80,
      onHeaderClick: () => handleSort("status"),
      headerTitle: "クリックでソート",
    },
    {
      key: "next_delivery_date",
      header: sortHeader("次の納品日", "next_delivery_date"),
      width: 140,
      minWidth: 96,
      onHeaderClick: () => handleSort("next_delivery_date"),
      headerTitle: "クリックでソート",
    },
    {
      key: "updated_at",
      header: sortHeader("最終更新日", "updated_at"),
      width: 128,
      minWidth: 96,
      onHeaderClick: () => handleSort("updated_at"),
      headerTitle: "クリックでソート",
    },
    {
      key: "media_item_count",
      header: sortHeader("メディア", "media_item_count"),
      width: 88,
      minWidth: 64,
      onHeaderClick: () => handleSort("media_item_count"),
      headerTitle: "総メディアアイテム数（クリックでソート）",
    },
    {
      key: "max_take_count",
      header: sortHeader("テイク", "max_take_count"),
      width: 80,
      minWidth: 64,
      onHeaderClick: () => handleSort("max_take_count"),
      headerTitle: "最大テイク数（クリックでソート）",
    },
    {
      key: "rating",
      header: sortHeader("評価", "rating"),
      width: 140,
      minWidth: 80,
      onHeaderClick: () => handleSort("rating"),
      headerTitle: "クリックでソート",
    },
    ...(canAddShot
      ? [
          {
            key: "actions",
            width: ACTIONS_COL_WIDTH,
            minWidth: ACTIONS_COL_WIDTH,
            resizable: false,
          } as TableColumn,
        ]
      : []),
  ];

  const columnCount = columns.length;

  return (
    <div className="u-col u-gap8 u-w100" style={{ marginBottom: 96 }}>
      <TopAppBarSubRow belowProjectBanner>
        <FilterChipBar
          items={shotTags}
          selectedId={selectedTagId}
          onSelect={setSelectedTagId}
          onReorder={handleTagReorder}
        />
      </TopAppBarSubRow>

      <div className="u-row-between u-items-center u-w100">
        <span className="bodytext-13 u-text-light-gray">
          {selectedTagId
            ? `${visibleRows.length}ショット`
            : `全${rows.length}ショット`}
        </span>
        <div className="u-row u-items-center u-gap4">
          <IconButton
            icon="content_copy"
            size={20}
            title="表をコピー（Excel / スプレッドシートに貼り付け可能）"
            ariaLabel="表をコピー"
            onClick={copyTableToClipboard}
          />
          {canEdit && <HelpHint description={TABLE_HELP} />}
        </div>
      </div>

      {/* 一括適用中のローディングを重ねるだけの器（余白・枠は持たせない） */}
      <div className="u-w100 u-relative">
        <Table
          columns={columns}
          minWidth={964}
          scrollAreaProps={{
            // 表のコピー / 貼り付けを受けるためフォーカス可能にする
            tabIndex: 0,
            onCopy: handleCopy,
            onPaste: handlePaste,
          }}
        >
          {visibleRows.length === 0 && (
            <tr>
              <td colSpan={columnCount}>
                <p className="bodytext-13 u-text-light-gray u-text-center u-ptb16">
                  {selectedTagId
                    ? "このタグのショットはありません"
                    : `ショットがありません${canAddShot ? "。下の入力欄からショットを追加できます。" : ""}`}
                </p>
              </td>
            </tr>
          )}
          {visibleRows.map((shot, index) => (
            <ShotTableRow
              key={shot.id}
              shot={shot}
              rowIndex={index}
              canEdit={canEdit}
              canClientEdit={canClientEdit}
              canRate={canRateInline}
              isClientGroup={isClientGroup}
              canMove={canMove}
              onPatch={handlePatch}
              onRequestDelete={handleRequestDelete}
              onRequestMove={setMoveSourceShot}
              onRate={handleRate}
              onClearRating={handleClearRating}
              onOpenPreview={handleOpenPreview}
            />
          ))}
          {canAddShot && !selectedTagId && (
            <tr>
              <td colSpan={columnCount}>
                <div className="u-row u-items-center u-gap8">
                  <span
                    className="material-symbols-rounded"
                    style={{ fontSize: 16, color: "var(--soremo-light-gray)" }}
                  >
                    add
                  </span>
                  <input
                    type="text"
                    placeholder="ショット名を入力して Enter で追加"
                    value={newShotName}
                    onChange={(e) => setNewShotName(e.target.value)}
                    onKeyDown={handleAddKeyDown}
                    disabled={isCreating}
                    style={ADD_INPUT_STYLE}
                  />
                </div>
              </td>
            </tr>
          )}
        </Table>
        {isBulkApplying && (
          <BulkOverlay>
            <SvgLoadingAnimation />
          </BulkOverlay>
        )}
      </div>

      {/* 削除確認 */}
      {deleteTarget && (
        <ConfirmModal
          message={`「${deleteTarget.shot_name}」を削除しますか？\nこの操作は取り消せません。`}
          confirmLabel="削除"
          loading={isDeleting}
          onConfirm={handleDeleteConfirmed}
          onCancel={() => setDeleteTarget(null)}
        />
      )}

      {/* ショット間移動ウィザード */}
      {moveSourceShot && projectId && (
        <ShotMoveWizardModal
          projectId={projectId}
          sourceShot={moveSourceShot}
          shots={rows}
          onClose={() => setMoveSourceShot(null)}
          onMoved={loadAllShots}
        />
      )}
    </div>
  );
};

export default ShotTable;
